import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import adminApi from '../../api/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const emptyProject = {
    title: '',
    subtitle: '',
    category: 'Healthcare',
    client: '',
    description: '',
    videoUrl: '',
    thumbnailUrl: '',
    color: '#FF4500',
    services: [],
    duration: '',
    year: new Date().getFullYear().toString(),
    impact: '',
    challenge: '',
    solution: '',
    results: ''
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await adminApi.getProjects();
      setProjects(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load projects",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (projectData) => {
    try {
      if (projectData.id) {
        await adminApi.updateProject(projectData.id, projectData);
        toast({ title: "Success", description: "Project updated" });
      } else {
        await adminApi.createProject(projectData);
        toast({ title: "Success", description: "Project created" });
      }
      setEditingProject(null);
      setIsCreating(false);
      loadProjects();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save project",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    
    try {
      await adminApi.deleteProject(projectId);
      toast({ title: "Success", description: "Project deleted" });
      loadProjects();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  if (isCreating || editingProject) {
    return (
      <ProjectForm
        project={editingProject || emptyProject}
        onSave={handleSave}
        onCancel={() => {
          setEditingProject(null);
          setIsCreating(false);
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-black uppercase mb-2">Projects</h1>
          <p className="text-gray-600">Manage your portfolio projects</p>
        </div>
        <Button
          onClick={() => setIsCreating(true)}
          className="bg-black text-white hover:bg-gray-900 font-bold"
        >
          <Plus className="mr-2" size={18} />
          Add Project
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden group hover:border-black transition-colors"
          >
            <div className="aspect-video bg-gray-200 relative">
              {project.thumbnailUrl && (
                <img
                  src={project.thumbnailUrl}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  onClick={() => setEditingProject(project)}
                  className="bg-white text-black hover:bg-gray-200"
                >
                  <Edit size={16} />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(project.id)}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs font-bold text-gray-500 uppercase mb-1">
                {project.category}
              </div>
              <h3 className="text-lg font-black uppercase mb-1">{project.title}</h3>
              <p className="text-sm text-gray-600">{project.subtitle}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {projects.length === 0 && (
        <div className="text-center py-20 text-gray-500">
          <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
          <p>No projects yet. Click "Add Project" to get started!</p>
        </div>
      )}
    </div>
  );
};

const ProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState(project);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await adminApi.uploadImage(file);
      handleChange('thumbnailUrl', `${process.env.REACT_APP_BACKEND_URL}${result.url}`);
      toast({ title: "Success", description: "Image uploaded" });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-black uppercase">
          {project.id ? 'Edit Project' : 'New Project'}
        </h1>
        <Button
          onClick={onCancel}
          variant="outline"
          className="border-2 border-black"
        >
          <X className="mr-2" size={18} />
          Cancel
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg border-2 border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Project Title *</Label>
            <Input
              required
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="border-2 border-black h-12"
              placeholder="PROJECT NAME"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Subtitle *</Label>
            <Input
              required
              value={formData.subtitle}
              onChange={(e) => handleChange('subtitle', e.target.value)}
              className="border-2 border-black h-12"
              placeholder="Client or Tagline"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Category *</Label>
            <select
              required
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full border-2 border-black h-12 px-3 rounded-md font-semibold"
            >
              <option>Healthcare</option>
              <option>Real Estate</option>
              <option>Podcast</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Client *</Label>
            <Input
              required
              value={formData.client}
              onChange={(e) => handleChange('client', e.target.value)}
              className="border-2 border-black h-12"
            />
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs font-bold uppercase mb-2 block">Description *</Label>
            <Textarea
              required
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="border-2 border-black"
              rows={3}
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Thumbnail Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploading}
              className="border-2 border-black h-12"
            />
            {formData.thumbnailUrl && (
              <img src={formData.thumbnailUrl} alt="Preview" className="mt-2 h-20 object-cover rounded" />
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Video URL (Vimeo)</Label>
            <Input
              value={formData.videoUrl}
              onChange={(e) => handleChange('videoUrl', e.target.value)}
              className="border-2 border-black h-12"
              placeholder="https://player.vimeo.com/video/..."
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Duration</Label>
            <Input
              value={formData.duration}
              onChange={(e) => handleChange('duration', e.target.value)}
              className="border-2 border-black h-12"
              placeholder="2:30"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Year</Label>
            <Input
              value={formData.year}
              onChange={(e) => handleChange('year', e.target.value)}
              className="border-2 border-black h-12"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Impact (e.g., +45% bookings)</Label>
            <Input
              value={formData.impact}
              onChange={(e) => handleChange('impact', e.target.value)}
              className="border-2 border-black h-12"
            />
          </div>

          <div>
            <Label className="text-xs font-bold uppercase mb-2 block">Color Accent</Label>
            <Input
              type="color"
              value={formData.color}
              onChange={(e) => handleChange('color', e.target.value)}
              className="border-2 border-black h-12"
            />
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-4">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            className="border-2 border-gray-300"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-black text-white hover:bg-gray-900 font-bold"
          >
            <Save className="mr-2" size={18} />
            Save Project
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminProjects;
